import { v4 as uuid } from 'uuid'
import { useSetFlowTo, normalizePath } from 'Simple/Flow.js'
import { notifyError, useNotifications } from 'Logic/Notifications.js'
import mutation from './mutation.graphql.js'
import { useMutation } from 'Data/Api.js'

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props, data) {
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, executeMutation] = useMutation(mutation)
  let [, notify] = useNotifications()
  return async function onSubmit({ args, value, change }) {
    switch (args.type) {
      case 'create':
        return props.onCreate()
      case 'preupdate': {
        let mutationResponse = await executeMutation({
          id: value.event_id,
          type: value.event_type,
          actions: value.actions.map(action => sanitizeAction(action)),
          data: {
            value_to: value.appointment_status,
            appointment_type_ids: value.appointment_type_ids,
          },
        })
        if (mutationResponse.error) {
          notify(notifyError(`Something went wrong. Please try again later.`))
          return
        }
        if (
          mutationResponse.data.automations_analyze_action_updates
            .total_affected > 0
        ) {
          change(next => {
            next.update_impact =
              mutationResponse.data.automations_analyze_action_updates
          })
          setFlowTo(normalizePath(props.viewPath, './PreUpdate/Content'))
          return
        } else {
          return props.onUpdate()
        }
        return
      }
      case 'update': {
        return props.onUpdate()
      }
      case 'cancel':
        return props.onCancel()
      case 'addAction': {
        change(next => {
          let actions = Array.isArray(next.actions) ? [...next.actions] : []
          let insertIndex =
            typeof args.index === 'number' ? args.index : actions.length

          if (insertIndex < 0) insertIndex = 0
          if (insertIndex > actions.length) insertIndex = actions.length

          actions.splice(insertIndex + 1, 0, {
            id: uuid(),
            type: null,
            data: null,
            order: insertIndex + 1,
          })

          next.actions = actions.map((action, order) => ({
            ...action,
            order,
          }))
        })
        break
      }
      case 'addActionInWait': {
        change(next => {
          let actions = Array.isArray(next.actions) ? [...next.actions] : []
          if (!actions.length) return

          let startIndex = args.index

          if (startIndex < 0) startIndex = 0
          if (startIndex > actions.length - 1) startIndex = actions.length - 1

          let endIndex = startIndex + 1
          for (let i = startIndex + 1; i < actions.length; i++) {
            if (actions[i]?.type === 'wait') break
            endIndex = i + 1
          }

          actions.splice(endIndex, 0, {
            id: uuid(),
            type: null,
            data: null,
            order: endIndex,
          })

          next.actions = actions.map((action, order) => ({
            ...action,
            order,
          }))
        })
        break
      }
      case 'addActionWait': {
        change(next => {
          // default the wait to be relative to before
          // appointment booking date for appt reminders automations
          let wait_metadata =
            value.event_type === 'appointment_bookings_state_update' &&
            Array.isArray(value.appointment_status) &&
            value.appointment_status.length === 1 &&
            value.appointment_status[0] === 'SCHEDULED'
              ? {
                  type: 'before',
                  event: 'booking_scheduled',
                }
              : {
                  type: 'after',
                  event: 'automation_triggered',
                }

          next.actions.push(
            {
              id: uuid(),
              type: 'wait',
              data: {
                weeks: 0,
                days: 0,
                hours: 0,
                minutes: 0,
                ...wait_metadata,
              },
              order: value.actions.length,
            },
            {
              id: uuid(),
              type: null,
              data: null,
              order: value.actions.length + 1,
            }
          )
        })
        break
      }
      case 'removeAction': {
        change(next => {
          let actions = [...value.actions]
          let target = actions[args.index]

          if (target?.type === 'wait') {
            let endIndex = args.index

            for (let i = args.index + 1; i < actions.length; i++) {
              if (actions[i]?.type === 'wait') break
              endIndex = i
            }

            actions.splice(args.index, endIndex - args.index + 1)
          } else {
            actions.splice(args.index, 1)
          }

          next.actions = actions
        })
        break
      }
      default:
        return
    }
  }
}

function sanitizeAction(action) {
  if (!action || typeof action !== 'object') return action
  // drop the extra keys used in the UI for internal tracking
  let {
    lastGroupItem,
    displayOrder,
    level,
    singleGroupItem,
    action_executions,
    showLastModified,
    updated_by_user,
    updated_at,
    ...rest
  } = action
  return rest
}
